import type { Zustand } from './StatusBadge';
import { formatiereZahl } from './WertEinheit';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * FACHSTATUS → STATUSMARKE (TX-STANDARD S1.5, Paket TX1)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Alle Bahnen bilden fachliche Status GLEICH auf die fünf Zustände der
 * `StatusBadge` ab. Vorher übersetzte jede Seite selbst (`zustandAusFarbe` in
 * `api.ts`, `farbzustand` in `koordination/gemeinsam.ts`, Hexwerte in den
 * Seiten) - und „unbestätigt“ war auf der einen Seite gelb, auf der anderen
 * grau.
 *
 * Jede Funktion liefert `{ zustand, text }` und passt so unmittelbar in die
 * Marke:
 *
 *     <StatusBadge {...pruefergebnis('nicht_pruefbar')} klein />
 *
 * Text ist immer dabei - Zustand nie nur über Farbe (S1.10).
 */

export interface Fachstatus {
  zustand: Zustand;
  text: string;
}

/** Ergebnis einer Prüfung gegen eine Grenze oder Regel. */
export type Pruefergebnis = 'erfuellt' | 'nicht_erfuellt' | 'nicht_pruefbar' | 'hinweis';

export function pruefergebnis(w: Pruefergebnis): Fachstatus {
  switch (w) {
    case 'erfuellt': return { zustand: 'gut', text: 'erfüllt' };
    case 'nicht_erfuellt': return { zustand: 'fehler', text: 'nicht erfüllt' };
    case 'nicht_pruefbar': return { zustand: 'warnung', text: 'nicht prüfbar' };
    default: return { zustand: 'info', text: 'Hinweis' };
  }
}

/** Rang eines Befunds: F, W, H (wie in den Rechenkernen). */
export type Befundrang = 'F' | 'W' | 'H';

export function befundrang(r: Befundrang): Fachstatus {
  switch (r) {
    case 'F': return { zustand: 'fehler', text: 'Fehler' };
    case 'W': return { zustand: 'warnung', text: 'Warnung' };
    default: return { zustand: 'info', text: 'Hinweis' };
  }
}

/** Bestätigung einer Vorgabe durch die Fachplanung. */
export function bestaetigung(bestaetigt: boolean): Fachstatus {
  return bestaetigt
    ? { zustand: 'gut', text: 'bestätigt' }
    : { zustand: 'warnung', text: 'unbestätigt' };
}

const HERKUNFT_TEXT: Record<string, string> = {
  modell: 'Modell',
  daten: 'Datenblatt',
  datenblatt: 'Datenblatt',
  hand: 'von Hand',
  quelle: 'Quelle',
  vorgabe: 'Vorgabe',
  annahme: 'Annahme',
  schaetzung: 'Schätzung',
};

/** Herkünfte, die einen Wert nur ANNEHMEN - sie verdienen einen zweiten Blick. */
const ANGENOMMEN = new Set(['vorgabe', 'annahme', 'schaetzung']);

/**
 * Herkunft eines Werts. Belegte Herkunft (Modell, Datenblatt, Hand, Quelle)
 * bleibt neutral; angenommene (Vorgabe, Annahme, Schätzung) ist eine Warnung.
 * Eine unbekannte Herkunft bleibt neutral und zeigt ihren Rohtext.
 */
export function herkunft(h: string | null | undefined): Fachstatus {
  const schluessel = (h ?? '').trim().toLowerCase();
  if (schluessel === '') return { zustand: 'neutral', text: 'ohne Herkunft' };
  const text = HERKUNFT_TEXT[schluessel] ?? (h ?? '').trim();
  return { zustand: ANGENOMMEN.has(schluessel) ? 'warnung' : 'neutral', text };
}

/**
 * Vollständigkeit „n von m (p %)“. Ohne Grenze der Bahn bleibt sie neutral
 * (eine Karte, die nur zählt, bekommt keine Ampel); mit Grenze ist sie ab der
 * Grenze `gut`, darunter `warnung`.
 */
export function vollstaendigkeit(n: number, m: number, grenzeProzent?: number): Fachstatus {
  const p = m > 0 ? (n / m) * 100 : 0;
  const text = `${formatiereZahl(n, 0)} von ${formatiereZahl(m, 0)} (${formatiereZahl(p, 1)} %)`;
  if (grenzeProzent === undefined || m === 0) return { zustand: 'neutral', text };
  return { zustand: p >= grenzeProzent ? 'gut' : 'warnung', text };
}

/**
 * Rechenstand eines Laufs gegen den Modellstand. Ein Lauf, der älter ist als
 * das Modell, ist „veraltet“ - seine Zahlen gehören zu einem anderen Stand.
 */
export function rechenstand(
  gerechnetAm: string | Date | null | undefined,
  modellstand?: string | Date | null,
): Fachstatus {
  if (!gerechnetAm) return { zustand: 'neutral', text: 'nicht gerechnet' };
  const lauf = new Date(gerechnetAm).getTime();
  const modell = modellstand ? new Date(modellstand).getTime() : Number.NaN;
  if (Number.isFinite(modell) && Number.isFinite(lauf) && lauf < modell) {
    return { zustand: 'warnung', text: 'veraltet' };
  }
  return { zustand: 'gut', text: 'aktuell' };
}

/** Freigabestand einer Berichtsfassung (Berichtsregister). */
export type Berichtsfassung = 'Vorabzug' | 'Zwischenstand' | 'Freigegeben';

export function berichtsfassung(s: Berichtsfassung): Fachstatus {
  switch (s) {
    case 'Vorabzug': return { zustand: 'warnung', text: 'Vorabzug' };
    case 'Zwischenstand': return { zustand: 'info', text: 'Zwischenstand' };
    default: return { zustand: 'gut', text: 'freigegeben' };
  }
}

/**
 * Koordinationsaufgabe. Offen ist eine Warnung, NIE ein Fehler: Rot bleibt dem
 * tatsächlichen Problem vorbehalten (Auftrag 17.09.2026, Abschnitt 18).
 */
export function koordination(offen: boolean): Fachstatus {
  return offen
    ? { zustand: 'warnung', text: 'offen' }
    : { zustand: 'gut', text: 'entschieden' };
}

/**
 * „entfällt“ (TX-STANDARD 1.3, Anmeldung TV): Die Anforderung trifft nicht zu.
 * Eine fachliche Aussage, keine Datenlücke - deshalb neutral und getrennt von
 * „nicht prüfbar“ (Warnung). Der Grund ist Pflicht und steht im `titel` UND
 * muss auf der Seite sichtbar daneben stehen (Spalte oder Zeile darunter);
 * ein Grund nur im Tooltip genügt nicht (S1.10).
 */
export function entfaellt(grund: string): Fachstatus & { titel: string } {
  const g = grund.trim();
  if (g === '') throw new Error('„entfällt“ braucht einen Grund (TX-STANDARD S1.5).');
  return { zustand: 'neutral', text: 'entfällt', titel: g };
}
